import React, {
  Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  View, StyleSheet, Text, TouchableOpacity,
} from 'react-native';
import Modal from 'react-native-modal';
import { GestureHandlerRootView, Pressable } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import DraggableFlatList, { ScaleDecorator } from 'react-native-draggable-flatlist';
import Colors from '../../../../../../constants/Colors';
import CloseModalIcon from '../../../../../CloseModalIcon';
import Style from '../../../../../../constants/Style';
import DotsRow from '../../DotsRow';
import Button from '../../../../../Button';
import { TemplateIssue } from '../../../../../../constants/Types';

interface IssueReorderModalProps {
  issues: TemplateIssue[],
  setIssues: Dispatch<SetStateAction<TemplateIssue[]>>,
  hide: () => void,
  visible: boolean,
}

interface IssueItemProps {
  title: string,
  drag: () => void,
  isActive: boolean,
}

const IssueItem = ({
  title, drag, isActive,
}: IssueItemProps) => <ScaleDecorator>
    <Pressable
      style={[styles.issueItem, isActive ? styles.draggingStyle : styles.receivingStyle]}
      onLongPress={drag}
      disabled={isActive}
    >
        <View style={Style.row}>
          <Text style={styles.issueTitle}>
            {title?.length ? title : 'New issue'}
          </Text>
        </View>
        <DotsRow />
    </Pressable>
  </ScaleDecorator>;

const IssueReorderModal = ({
  issues, hide, visible, setIssues,
}: IssueReorderModalProps) => {
  const [issueList, setIssueList] = useState<TemplateIssue[]>(issues);

  const insets = useSafeAreaInsets();

  useEffect(() => setIssueList(issues), [visible]);

  const filteredIssues = useMemo(
    () => issueList?.filter((issue) => issue.Text.toLowerCase() !== 'other'),
    [issueList],
  );

  const keyExtractor = useCallback((item: any, index: number) => `${item.Id}-${index}`, []);

  return <Modal
    style={Style.modal}
    isVisible={visible}
    onBackButtonPress={hide}
    onBackdropPress={hide}
    onSwipeComplete={hide}
  >
    <View style={[styles.container, { paddingBottom: insets.bottom || 24 }]}>
      <TouchableOpacity
        style={[Style.closeButton, { top: insets.top }]}
        onPress={hide}
      >
        <CloseModalIcon />
      </TouchableOpacity>
      <>
        <Text style={[styles.title, { marginTop: insets.top }]}>Reorder Issues</Text>
        <GestureHandlerRootView>
          <DraggableFlatList
            data={filteredIssues}
            renderItem={({ item, isActive, drag }) => (
              <IssueItem
                title={item.Text}
                drag={drag}
                isActive={isActive} />)}
            keyExtractor={keyExtractor}
            onDragEnd={({ data }) => setIssueList(data)} />
        </GestureHandlerRootView>
      </>
      <View style={styles.buttonContainer}>
        <Button
          title='Cancel'
          onPress={hide}
        />
        <Button
          title='Save'
          affirmative
          onPress={() => {
            setIssues(issueList);
            hide();
          }}
        />
      </View>
    </View>
  </Modal>;
};

const styles = StyleSheet.create({
  container: {
    height: '100%',
    width: '100%',
    backgroundColor: '#FFF',
    alignItems: 'center',
    paddingTop: 60,
  },
  title: {
    fontSize: 28,
    lineHeight: 35,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    textAlign: 'center',
    marginBottom: 32,
    alignSelf: 'center',
  },
  issueItem: {
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginHorizontal: 24,
    marginBottom: 12,
    backgroundColor: Colors.light.hereworksWhite200,
  },
  issueTitle: {
    fontSize: 16,
    lineHeight: 24,
    paddingHorizontal: 6,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  receivingStyle: {
    backgroundColor: Colors.light.hereworksBlack50,
    borderRadius: 12,
  },
  draggingStyle: {
    backgroundColor: Colors.light.hereworksBlack100,
    borderRadius: 12,
  },
  stretch: {
    alignSelf: 'stretch',
    flex: 1,
  },
  buttonContainer: {
    flexDirection: 'row',
    paddingHorizontal: 24,
  },
});

export default IssueReorderModal;
